// Pricing Simulations Hook
// Manages CRUD operations for pricing simulations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { toast } from 'sonner'
import { TaxRegime } from '@/lib/calculator-calculations'

export interface PricingSimulation {
  id: string
  user_id: string
  name: string
  monthly_goal: number
  weekly_work_hours: number
  weeks_per_month: number
  tax_percentage: number
  vacation_percentage: number
  thirteenth_percentage: number
  fixed_monthly_costs: number
  tax_regime: TaxRegime
  calculated_hourly_rate: number | null
  created_at: string
  updated_at: string
}

export interface PricingSimulationInsert {
  name?: string
  monthly_goal: number
  weekly_work_hours?: number
  weeks_per_month?: number
  tax_percentage?: number
  vacation_percentage?: number
  thirteenth_percentage?: number
  fixed_monthly_costs?: number
  tax_regime?: TaxRegime
  calculated_hourly_rate?: number
}

/**
 * Hook to fetch all user's pricing simulations
 */
export function usePricingSimulations() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['pricing-simulations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing_simulations')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as PricingSimulation[]
    },
    enabled: !!user
  })
}

/**
 * Hook to fetch a single simulation
 */
export function usePricingSimulation(id: string | null) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['pricing-simulations', id],
    queryFn: async () => {
      if (!id) return null
      
      const { data, error } = await supabase
        .from('pricing_simulations')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data as PricingSimulation | null
    },
    enabled: !!user && !!id
  })
}

/**
 * Hook to fetch the most recent simulation
 */
export function useLatestSimulation() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['pricing-simulations', 'latest'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing_simulations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as PricingSimulation | null
    },
    enabled: !!user
  })
}

/**
 * Hook to create a new simulation
 */
export function useCreateSimulation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (simulation: PricingSimulationInsert) => {
      if (!user) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('pricing_simulations')
        .insert({
          ...simulation,
          user_id: user.id
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-simulations'] })
      toast.success('Simulação salva com sucesso!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar simulação: ${error.message}`)
    }
  })
}

/**
 * Hook to update an existing simulation
 */
export function useUpdateSimulation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PricingSimulationInsert> & { id: string }) => {
      const { data, error } = await supabase
        .from('pricing_simulations')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricing-simulations'] })
      queryClient.invalidateQueries({ queryKey: ['pricing-simulations', variables.id] })
      toast.success('Simulação atualizada!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar simulação: ${error.message}`)
    }
  })
}

/**
 * Hook to delete a simulation
 */
export function useDeleteSimulation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pricing_simulations')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-simulations'] })
      toast.success('Simulação excluída!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir simulação: ${error.message}`)
    }
  })
}
